import type { IconType } from '@/lib/icons'
import { KeyRound, ShieldAlert, TimerReset } from '@/lib/icons'
import { AuthErrorCode } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { closeMiniApp } from '@/lib/telegram'

interface AuthErrorScreenProps {
  code: AuthErrorCode
  /** `isInsideTelegram()` natijasi — MISSING/INVALID xabarini kontekstga qarab moslashtiradi. */
  insideTelegram: boolean
  onRetry: () => void
}

interface ScreenContent {
  icon: IconType
  title: string
  description: string
  action: { label: string; onClick: () => void } | null
}

function getContent(
  code: AuthErrorCode,
  insideTelegram: boolean,
  onRetry: () => void,
): ScreenContent {
  switch (code) {
    case AuthErrorCode.EXPIRED:
      return {
        icon: TimerReset,
        title: 'Sessiya tugadi',
        description: 'Botga qaytib, panelni qayta oching.',
        action: { label: 'Yopish', onClick: closeMiniApp },
      }
    case AuthErrorCode.NOT_ADMIN:
      return {
        icon: ShieldAlert,
        title: "Sizda ruxsat yo'q",
        description:
          'Bu panel faqat administratorlar uchun ochiq. Agar bu xato deb hisoblasangiz, bosh administratorga murojaat qiling.',
        action: null,
      }
    // AUTH_MISSING va AUTH_INVALID — bir xil oila: initData umuman yo'q yoki tekshiruvdan
    // o'tmadi. Ikkalasi ham ko'pincha xuddi shu sababdan kelib chiqadi (panel Telegram
    // tashqarisida ochilgan yoki eskirgan havola orqali), shuning uchun bitta ekran yetarli.
    case AuthErrorCode.MISSING:
    case AuthErrorCode.INVALID:
      return {
        icon: KeyRound,
        title: "Sessiyani tekshirib bo'lmadi",
        description: insideTelegram
          ? 'Panelni ochishda muammo yuz berdi. Botga qaytib, "Admin panel" tugmasini qayta bosing.'
          : 'Bu sahifa faqat Telegram ilovasi ichida ishlaydi. Siz hozir uni brauzerda ochyapsiz — botdan "Admin panel" tugmasini bosing.',
        action: { label: 'Qayta urinish', onClick: onRetry },
      }
    default:
      return {
        icon: KeyRound,
        title: "Sessiyani tekshirib bo'lmadi",
        description: 'Iltimos, panelni botdan qayta oching.',
        action: { label: 'Qayta urinish', onClick: onRetry },
      }
  }
}

/**
 * `AUTH_*` kodlari uchun to'liq ekran holati — `docs/03-app-flow.md` "Xato holatlari"
 * jadvaliga mos. AppShell'siz (navigatsiyasiz) ko'rsatiladi: admin bo'lmagan/tasdiqlanmagan
 * foydalanuvchiga ko'rsatiladigan menyu yo'q.
 */
export function AuthErrorScreen({ code, insideTelegram, onRetry }: AuthErrorScreenProps) {
  const content = getContent(code, insideTelegram, onRetry)
  const Icon = content.icon

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
      <div className="flex size-16 items-center justify-center rounded-full bg-app-card text-app-hint">
        <Icon size={28} aria-hidden="true" />
      </div>
      <h1 className="text-lg font-semibold">{content.title}</h1>
      <p className="max-w-sm text-sm text-app-hint">{content.description}</p>
      {content.action && (
        <Button className="mt-2" onClick={content.action.onClick}>
          {content.action.label}
        </Button>
      )}
    </div>
  )
}
